import React, { useEffect, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core";
import DefaultTable from "components/Table";
import { popupNotification, useLayoutDispatch } from "context/LayoutContext";
import useStyles from "pages/containers/styles";
import { importEcommerceShipment } from "api/shipment";
import { ClipboardCopy, Close, Search, Warning } from "icons";
import { Alert } from "@material-ui/lab";
import * as XLSX from "xlsx";

const shippingOptions = [
  "FEDEX",
  "FEDEX 2 DAY",
  "FEDEX EXP SVR",
  "FEDEX FRST OVRN",
  "FEDEX GROUND",
  "FEDEX GRND ECO",
  "FEDEX HOME DELV",
  "FEDEX PRIO OVRN",
  "FEDEX STND OVRN",
  "FEDEX SMARTPOST",
  "UPS 2D AIR COM",
  "UPS 2NDD AIR",
  "UPS 2NDD AIR AM",
  "UPS 3 DAY SLCT",
  "UPS FREIGHT",
  "UPS GROUND",
  "UPS NXD AIR",
  "UPS NXD AIR SVR",
  "UPS NDA SVR COM",
  "UPS STANDART",
  "UPS SUREPOST",
  "UPS EXPRESS",
  "UPS WORLDWIDE SAVER",
  "UPS WORLDWIDE EXPEDITED",
];

const FedexUpsModal = ({ open, onClose }) => {
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const filteredOptions = shippingOptions.filter((option) =>
    option.toLowerCase().includes(search.toLowerCase()),
  );

  const handleCopy = async (option) => {
    try {
      await navigator.clipboard.writeText(option);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle>
        Shipping Methods
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 400 }}>
        <TextField
          fullWidth
          autoFocus
          variant="outlined"
          placeholder="Search shipping methods..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <List>
          {filteredOptions.map((option, index) => (
            <ListItem
              key={index}
              onClick={() => handleCopy(option)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <ListItemText>
                {option}
                {hoveredIndex === index && (
                  <IconButton edge="end" aria-label="copy">
                    <ClipboardCopy color="#000" />
                  </IconButton>
                )}
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Copied!
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

const formatShipMethod = (ship_method) => {
  return ship_method
    .toUpperCase()
    .replaceAll("_", " ")
    .replaceAll("SELECT", "SLCT")
    .replaceAll("STANDARD OVERNIGHT", "STND OVRN")
    .replaceAll("FIRST OVERNIGHT", "FRST OVRN")
    .replaceAll("EXPRESS SAVER", "EXP SVR")
    .replaceAll("DELIVERY", "DELV");
};

const formatPhone = (phone) => {
  if (!phone) return phone;
  return phone
    .replaceAll("+1", "")
    .replaceAll(" ", "")
    .replaceAll("+", "")
    .replaceAll("-", "")
    .replaceAll("(", "")
    .replaceAll(")", "")
    .replaceAll(",", "");
};

const exportErroredPOsToExcel = (erroredPOs, originalItems, errorByPO) => {
  const filtered = originalItems.filter((item) =>
    erroredPOs.includes(item["PO Number"]),
  );
  const exportData = [];
  filtered.forEach((i) => {
    exportData.push({
      ...i,
      Errors: errorByPO?.[i["PO Number"]],
    });
  });

  // Excel için veri hazırla
  // Sadece istediğiniz headerları seçin
  const headers = [
    "Marketplace",
    "Ship Method",
    "PO Number",
    "Ship Via",
    "Customer Name",
    "Phone",
    "Line 1",
    "Line 2",
    "City",
    "State",
    "Country",
    "Zip Code",
    "SKU",
    "Quantity",
    "Ref 1",
    "Ref 2",
    "Customer Order Number",
    "Customer Part Number",
    "Errors",
  ];

  // Header sırasına göre exportData'yı yeniden düzenle
  const exportDataWithHeaders = exportData.map((row) => {
    const newRow = {};
    headers.forEach((header) => {
      newRow[header] = row[header] ?? "";
    });
    return newRow;
  });

  const ws = XLSX.utils.json_to_sheet(exportDataWithHeaders, {
    header: headers,
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Errored POs");
  XLSX.writeFile(wb, "errored_pos.xlsx");
};

export default function ShipmentImport({
  shipments,
  open,
  close,
  duplicates = [],
  is_ltl = false,
}) {
  const classes = useStyles();
  const layoutDispatch = useLayoutDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [showServiceCodes, setShowServiceCodes] = useState(false);
  const [errors, setErrors] = useState({});
  const [erroredPOItems, setErroredPOItems] = useState([]);
  const [errorByPO, setErrorByPO] = useState({});
  const [importStats, setImportStats] = useState({});

  useEffect(() => {
    if (shipments && shipments.items) {
      const uniquePOs = _.uniqBy(shipments.items, (item) => item["PO Number"])
        .length;
      const skuCount = _.uniqBy(shipments.items, (item) => item["SKU"]).length;
      const totalQty = shipments.items.reduce((acc, item) => {
        return acc + (parseInt(item["Quantity"]) || 0);
      }, 0);
      console.log(skuCount, totalQty, uniquePOs);
      setImportStats({ skuCount, totalQty, uniquePOs });
    }
  }, [shipments]);

  const headers = [
    {
      id: "line_number",
      label: "Line Number",
      render: (r) => r["Line Number"],
    },
    {
      id: "po_number",
      label: "PO Number",
      render: (r) => <span title={r["PO Number"]}>{r["PO Number"]}</span>,
    },
    {
      id: "marketplace",
      label: "Marketplace / Ship Method",
      render: (r) => (
        <>
          <span title="Marketplace">{r["Marketplace"]}</span>
          <Typography variant="subtitle2" className={classes.subtitle}>
            <span title="Ship Method">{r["Ship Method"]}</span>
          </Typography>
        </>
      ),
    },
    {
      id: "sku",
      render: (row) => row["SKU"],
    },
    {
      id: "qty",
      render: (row) => row["Quantity"],
    },
    {
      id: "customer_name",
      label: "Customer",
      render: (r) => (
        <>
          <span title={r["Customer Name"]}>{r["Customer Name"]}</span>
          <Typography variant="subtitle2" className={classes.subtitle}>
            <span title="Phone">{r["Phone"]}</span>
          </Typography>
        </>
      ),
    },
    {
      id: "line_1",
      label: "Line 1",
      render: (row) => row["Line 1"],
    },
    {
      id: "line_2",
      label: "Line 2",
      render: (row) => row["Line 2"],
    },
    {
      id: "state",
      label: "Other",
      render: (r) => (
        <>
          <span title={r["City"]}>{r["City"]}</span>
          <Typography variant="subtitle2" className={classes.subtitle}>
            <span title="State">{r["State"]}</span> /{" "}
            <span title="Country">{r["Country"]}</span> /{" "}
            <span title="Zip Code">{r["Zip Code"]}</span>
          </Typography>
        </>
      ),
    },
  ];

  const handleClose = (status) => {
    close(status);
    setIsFetching(false);
    setErrors({});
  };

  const handleSubmit = async () => {
    const data = { shipments: [] };
    const groupedShipments = {};

    // Filter out previously errored POs if any
    const itemsToProcess =
      erroredPOItems.length > 0
        ? shipments.items.filter(
            (item) => !erroredPOItems.includes(item["PO Number"]),
          )
        : shipments.items;

    if (errors?.make_ltl_pos?.length > 0) {
      itemsToProcess.forEach((item) => {
        if (errors.make_ltl_pos.includes(item["PO Number"])) {
          item["Ship Method"] = "Updated to LTL";
          item["Ship Via"] = "Updated to LTL";
        }
      });
    }

    itemsToProcess.forEach((item) => {
      const shipVia = item["Ship Method"]?.toLowerCase() || "";

      let key;
      let writePO = false;

      if (shipVia.startsWith("fedex") || shipVia.startsWith("ups")) {
        key = `${item["Order Number"]}_${item["PO Number"]}_${item["Shipment Number"]}`;
      } else {
        const line1 = item["Line 1"]?.trim().toLowerCase();
        const line2 = (item["Line 2"] || "").trim().toLowerCase();
        const city = item["City"]?.trim().toLowerCase();
        const phone = item["Phone"]?.trim();
        key = `${line1}_${line2}_${city}_${phone}`;
        writePO = true;
      }

      if (!groupedShipments[key]) {
        groupedShipments[key] = {
          order_number: item["Order Number"],
          po_number: item["PO Number"],
          shipment_number: item["Shipment Number"],
          ship_via: formatShipMethod(
            item["Ship Method"] || item["Ship Via"] || "-",
          ),
          marketplace: item["Marketplace"],
          address: {
            customer: item["Customer Name"],
            line_1: item["Line 1"],
            line_2: item["Line 2"] || "",
            city: item["City"],
            state: item["State"],
            zip_code: item["Zip Code"],
            phone: formatPhone(item["Phone"]),
            country: item["Country"] || "US",
          },
          details: [],
        };
      }

      const detailItem = {
        sku: item["SKU"],
        quantity: parseInt(item["Quantity"]) || 0,
        seller_price: item["Price"] || 0,
        seller_order_number: item["Customer Order Number"] || "",
        seller_part_number: item["Customer Part Number"] || "",
        seller_ref_1: item["Ref 1"] || "",
        seller_ref_2: item["Ref 2"] || "",
      };

      if (writePO) {
        detailItem.seller_po_number = item["PO Number"];
      }

      groupedShipments[key].details.push(detailItem);
    });

    data.shipments = Object.values(groupedShipments);
    setIsFetching(true);

    importEcommerceShipment({
      data,
      responseSetter: ({ status, errors, message }) => {
        setIsFetching(false);

        if (status === "error" && message) {
          popupNotification({
            dispatch: layoutDispatch,
            message: message,
            status: "error",
          });
        }
        else if (status === "error" && errors) {
          setErrors(errors);
          if (errors.errored_pos && Array.isArray(errors.errored_pos)) {
            // Store errored POs for potential retry
            setErroredPOItems(errors.errored_pos);
            setErrorByPO(errors.error_by_po);
          }
          popupNotification({
            dispatch: layoutDispatch,
            message: "Error! You can retry with successful POs.",
            status: "error",
          });
        } else {
          // If successful and we had previous errors, now export the errored POs
          if (erroredPOItems.length > 0) {
            setTimeout(
              () =>
                exportErroredPOsToExcel(
                  erroredPOItems,
                  shipments.items,
                  errorByPO,
                ),
              500,
            );
          }

          popupNotification({
            dispatch: layoutDispatch,
            message: message || "Shipment(s) created successfully",
            status: "success",
          });
          handleClose(true);
        }
      },
    });
  };

  return (
    <Dialog onClose={() => handleClose()} open={open} maxWidth="lg" fullWidth>
      <DialogTitle>
        Import Shipment
        <span style={{ margin: 10 }}></span>
      </DialogTitle>
      <DialogContent>
        <FedexUpsModal
          open={showServiceCodes}
          onClose={() => setShowServiceCodes(false)}
        />
        {duplicates.length > 0 && (
          <Grid
            container
            spacing={2}
            style={{ border: "1px solid red", marginBottom: "1rem" }}
          >
            <Grid item>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Warning color="red" />{" "}
                <strong style={{ color: "red" }}> Duplicate Rows </strong>
              </div>
            </Grid>
            <Grid item>
              <strong>Duplicate rows removed: {duplicates.length}</strong>
            </Grid>
          </Grid>
        )}
        {Object.keys(errors).length > 0 && (
          <Grid container spacing={2} style={{ border: "1px solid red" }}>
            <Grid item>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Warning color="red" />{" "}
                <strong style={{ color: "red" }}> Import Errors</strong>
              </div>
            </Grid>
            {errors.no_customer.length > 0 && (
              <Grid item>
                <strong>Customer Name Error:</strong>
                <div>{errors.no_customer.join(", ")}</div>
              </Grid>
            )}
            {errors.no_product.length > 0 && (
              <Grid item>
                <strong>Products not found:</strong>
                <div>{errors.no_product.join(", ")}</div>
              </Grid>
            )}
            {errors.account_errors.length > 0 && (
              <Grid item>
                <strong>Ship method errors:</strong>
                <div>{errors.account_errors.join(", ")}</div>
              </Grid>
            )}
            {errors.no_dimensions.length > 0 && (
              <Grid item>
                <strong>Products without dimensions:</strong>
                <div>{errors.no_dimensions.join(", ")}</div>
              </Grid>
            )}
            {errors.no_us.length > 0 && (
              <Grid item>
                <strong>Not shipping to US:</strong>
                <div>{errors.no_us.join(", ")}</div>
              </Grid>
            )}
            {errors.no_inventory.length > 0 && (
              <Grid item>
                <strong>No Inventory:</strong>
                <div>{errors.no_inventory.join(", ")}</div>
              </Grid>
            )}
            {errors.po_number.length > 0 && (
              <Grid item>
                <strong>PO already imported:</strong>
                <div>{errors.po_number.join(", ")}</div>
              </Grid>
            )}
            {errors.size_errors.length > 0 && (
              <Grid item>
                <strong>
                  Shipment exceeds 108" and converted to LTL. Please send LTL
                  labels to proceed:
                </strong>
                <div>{errors.make_ltl_pos.join(", ")}</div>
              </Grid>
            )}
            {errors.others.length > 0 && (
              <Grid item>
                <strong>Unexpected Errors:</strong>
                <div>{errors.others.join(", ")}</div>
              </Grid>
            )}
          </Grid>
        )}
        <Grid container spacing={4}>
          {isFetching ? (
            <Grid item xs={12}>
              <CircularProgress />
            </Grid>
          ) : shipments?.count ? (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <DefaultTable
                headers={headers}
                data={shipments}
                headerCounts={{
                  po_number: importStats.uniquePOs,
                  sku: importStats.skuCount,
                  qty: importStats.totalQty,
                }}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="danger"
          onClick={() => handleClose()}
          disabled={isFetching}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isFetching || !shipments?.count}
        >
          {erroredPOItems.length > 0 ? "Skip Errors & Save" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
